/**
 * @aparte/provider-openai-compat — ONE adapter for every OpenAI-compatible
 * chat-completions endpoint.
 *
 * The OpenAI `/chat/completions` wire format is the de-facto industry standard:
 * OpenAI, Mistral, OpenRouter, Z.ai, Groq, Together, LM Studio, Ollama (`/v1`)
 * and many more all speak it. This package is the single, zero-dependency
 * format adapter for that family — vendors differ only by DATA (base URL, auth
 * header, branding), which you pass as config (or pick from `presets`).
 *
 * It replaces the per-vendor `@aparte/provider-{openai,mistral,zai,openrouter,
 * lmstudio,ollama}` packages, whose adapter bodies were byte-identical copies
 * (drift even produced real bugs: LM Studio dropped `max_tokens`, Z.ai dropped
 * `seed` — both fixed here by construction, there is only one body now).
 *
 * Model lists are CONSUMER data: pass `models` statically, or rely on the
 * generic `GET {baseURL}/models` fetcher (part of the compat standard). For
 * vendors outside this family (Anthropic, Gemini, …) use the AI-SDK bridge
 * provider instead — this package deliberately covers ONE format.
 */

import type {
    AparteAIProvider,
    AparteAIModel,
    AparteAIProviderConfigSchema,
    AparteChatRequest,
    AparteChatMessage,
    AparteTool,
    AparteToolCall,
    AparteContentPart,
    AparteStreamEvent,
    AparteUsage,
    AparteFormatAdapter,
} from '@aparte/core';
import { contentToText } from '@aparte/core';

// ─── Options ─────────────────────────────────────────────────────────────────

/** Config for one OpenAI-compatible endpoint. Everything but `id`/`baseURL` is branding/data. */
export interface OpenAICompatProviderOptions {
    /** Provider id used across aparté (key resolution, model picker, events). */
    id: string;
    /** Endpoint base, e.g. `https://api.openai.com/v1` or `http://localhost:11434/v1`. */
    baseURL: string;
    /** Display name (defaults to `id`). */
    name?: string;
    /** Brand icon (SVG string / data URI / icon-provider key). */
    icon?: string;
    /** Brand color. */
    color?: string;
    /** Short tag line. */
    description?: string;
    /** Where the user gets a key. */
    helpUrl?: string;
    /** Whether the vendor offers free models. */
    hasFreeModels?: boolean;
    /**
     * Local server (LM Studio, Ollama…): key optional, and the generic
     * `/models` fetch runs even without a key.
     */
    isLocal?: boolean;
    /** Static model list (consumer data). Defaults to `[]` — use `fetchModels`. */
    models?: AparteAIModel[];
    /**
     * Extra headers sent on every request (chat + model fetch), e.g.
     * OpenRouter's attribution headers `HTTP-Referer` / `X-Title`.
     */
    extraHeaders?: Record<string, string>;
    /** Override the default apiKey+endpoint settings schema. */
    configSchema?: AparteAIProviderConfigSchema;
}

// ─── Message / tool shaping (the format half) ────────────────────────────────

/** Content → OpenAI multipart array when images are present. */
function toOpenAIContent(content: string | AparteContentPart[]): unknown {
    if (typeof content === 'string') return content;
    // Two arms, exhaustive: a part is text or an image. A third used to answer the
    // removed `AparteFilePart` with an empty text part — a branch nothing could reach,
    // standing in for inline-file support this format does not have.
    return content.map(p => (p.type === 'text'
        ? { type: 'text', text: p.text }
        : { type: 'image_url', image_url: { url: p.image } }));
}

/**
 * The two roles a tool turn used to wear. `AparteChatMessage` no longer allows either, so
 * the comparison is on the role as a string: a message carrying one comes from code built
 * against an older aparté, which no type here can catch. Warned once per role, then
 * skipped — passing it on would put an unknown role on the wire and 400 the whole request.
 */
const LEGACY_TOOL_ROLES = new Set(['tool_call', 'tool_result']);
const warnedLegacyRoles = new Set<string>();
function warnLegacyToolRole(role: string): void {
    if (warnedLegacyRoles.has(role)) return;
    warnedLegacyRoles.add(role);
    console.warn(
        `[@aparte/provider-openai-compat] Skipping a message with the removed role "${role}".`
        + " A tool turn is now an assistant message carrying its calls — { role: 'assistant',"
        + " content: <what it said>, toolCalls } — followed by one { role: 'tool', content:"
        + ' <result>, toolCallId, toolName } per call. This warning is removed in 0.18.',
    );
}

// The other half of that legacy shape, and it is deliberately NOT warned: an assistant
// message built by hand with both `toolCalls` and image parts loses the images here.
// Chat Completions does not carry them — an assistant message's content parts are `text`
// and `refusal`, never `image_url` — so the mapping that used to emit them was a 400
// waiting to happen, and the loop cannot mint the shape (`stream-run.ts` builds an
// assistant's content from a string). Images on a USER turn are carried, unchanged.

/** AparteChatMessage[] → OpenAI messages (incl. an assistant turn's calls and the `tool` answers). */
function toOpenAIMessages(messages: AparteChatMessage[]): unknown[] {
    const out: unknown[] = [];
    for (const msg of messages) {
        const role: string = msg.role;
        if (LEGACY_TOOL_ROLES.has(role)) {
            warnLegacyToolRole(role);
            continue;
        }
        if (msg.role === 'tool') {
            out.push({ role: 'tool', tool_call_id: msg.toolCallId, content: contentToText(msg.content) });
            continue;
        }
        if (msg.role === 'assistant' && msg.toolCalls?.length) {
            out.push({
                role: 'assistant',
                // `||`, not `??`: this API takes null (or nothing) beside `tool_calls`, and
                // an assistant that said nothing before its calls now carries '' — which
                // some endpoints reject and the rest read as an empty reply.
                content: contentToText(msg.content) || null,
                tool_calls: msg.toolCalls.map(tc => ({
                    id: tc.id,
                    type: 'function',
                    function: { name: tc.name, arguments: JSON.stringify(tc.input) },
                })),
            });
            continue;
        }
        out.push({ role: msg.role, content: toOpenAIContent(msg.content) });
    }
    return out;
}

/** AparteTool[] → OpenAI function-tool declarations. */
function toOpenAITools(tools: AparteTool[]): unknown[] {
    return tools.map(t => ({
        type: 'function',
        function: { name: t.name, description: t.description, parameters: t.inputSchema },
    }));
}

/** Accept both raw keys and user-pasted `Bearer xxx` values (local servers). */
function bearer(key: string): string {
    return key.startsWith('Bearer ') ? key : `Bearer ${key}`;
}

const DEFAULT_CONFIG_SCHEMA = (opts: OpenAICompatProviderOptions): AparteAIProviderConfigSchema => ({
    fields: opts.isLocal
        ? [
            { id: 'endpoint', type: 'url', label: 'Server', defaultValue: opts.baseURL, required: true },
            { id: 'apiKey', type: 'password', label: 'API Key / Token (optional)', placeholder: 'Bearer ...', isAdvanced: true },
        ]
        : [
            { id: 'apiKey', type: 'password', label: 'API Key', placeholder: 'sk-...', required: true },
            { id: 'endpoint', type: 'url', label: 'Custom endpoint', placeholder: opts.baseURL, isAdvanced: true },
        ],
});

// ─── The factory ─────────────────────────────────────────────────────────────

/**
 * What {@link createOpenAICompatProvider} hands back: a provider whose whole
 * format-adapter surface is guaranteed present.
 *
 * `AparteAIProvider` declares that surface optional — correct in general, since a
 * provider may own its I/O through `chat()` instead — and `AparteFormatAdapter` keeps
 * `authHeaders` / `parseText` optional because some vendors authenticate by query
 * string or never answer non-streaming. THIS factory always supplies all of them, so
 * saying so here is the difference between a caller writing
 * `provider.buildRequest(req)` and a caller sprinkling `!` or writing a check that
 * cannot fail. Useful to annotate with when you drive the adapter yourself (your own
 * `fetch`, your own `AbortSignal`) from a server or an Electron main process.
 */
export type OpenAICompatProvider =
    AparteAIProvider
    & AparteFormatAdapter
    & Required<Pick<AparteFormatAdapter, 'authHeaders' | 'parseText'>>;

/**
 * Build an `AparteAIProvider` (full format-adapter surface) for one
 * OpenAI-compatible endpoint. Register it like any provider:
 *
 * ```ts
 * import { createOpenAICompatProvider, presets } from '@aparte/provider-openai-compat';
 * aparteGlobalConfig.registerAIProvider(createOpenAICompatProvider(presets.OPENROUTER));
 * // or any compat endpoint, no preset needed:
 * aparteGlobalConfig.registerAIProvider(createOpenAICompatProvider({ id: 'groq', baseURL: 'https://api.groq.com/openai/v1' }));
 * ```
 *
 * Returns {@link OpenAICompatProvider}: the full format-adapter surface, non-optional.
 */
export function createOpenAICompatProvider(opts: OpenAICompatProviderOptions): OpenAICompatProvider {
    const displayName = opts.name ?? opts.id;

    return {
        id: opts.id,

        getMetadata() {
            return {
                id: opts.id,
                name: displayName,
                icon: opts.icon,
                color: opts.color,
                description: opts.description,
                helpUrl: opts.helpUrl,
                hasFreeModels: opts.hasFreeModels,
                isLocal: opts.isLocal,
                configSchema: opts.configSchema ?? DEFAULT_CONFIG_SCHEMA(opts),
            };
        },

        getModels(): AparteAIModel[] {
            return opts.models ?? [];
        },

        /**
         * Generic `GET {baseURL}/models` — part of the compat standard. Cloud
         * endpoints need a key (returns `[]` without one); local servers fetch
         * keyless. Vendor-specific niceties (pricing, name prettifying) are
         * consumer concerns: pass `models` yourself for anything fancier.
         */
        async fetchModels(config?: string | Record<string, string>): Promise<AparteAIModel[]> {
            const apiKey = typeof config === 'string' ? config : config?.['apiKey'];
            const endpoint = (typeof config === 'object' ? config?.['endpoint'] : null) || opts.baseURL;
            if (!apiKey && !opts.isLocal) return [];

            try {
                const headers: Record<string, string> = { ...opts.extraHeaders };
                if (apiKey) headers['Authorization'] = bearer(apiKey);
                const response = await fetch(`${endpoint}/models`, { headers });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const data = await response.json() as { data?: Array<{ id: string; name?: string; context_length?: number }> };
                return (data.data ?? []).map(m => ({
                    id: m.id,
                    name: m.name || m.id,
                    contextWindow: m.context_length,
                    // `function_calling` is declared because it is a property of the
                    // WIRE FORMAT, which is what this provider is: `/chat/completions`
                    // takes a `tools` array, and every server behind a compat endpoint
                    // accepts one. `/models` returns `{id, object, owned_by}` and says
                    // nothing about tools, so waiting for it to declare the capability
                    // means never declaring it — and core gates tools on exactly this
                    // field, so a registered tool, an approval gate and the whole
                    // elicitation path were dead on this provider: the model was asked
                    // to use a tool it had never been sent.
                    //
                    // The failure mode of over-declaring is mild and visible: a model
                    // that cannot call tools simply does not call one. The failure mode
                    // of under-declaring was silent and total. A server that rejects a
                    // `tools` array outright surfaces as an error from its own endpoint,
                    // which is the right place for that argument to happen.
                    capabilities: ['streaming', 'function_calling'],
                }));
            } catch (error) {
                console.error(`[${displayName}] Failed to fetch models:`, error);
                return [];
            }
        },

        // ── Format-adapter surface (transport ⊥ format) ──────────────────────
        // The vendor concern only: request shape + stream parsing. Auth and
        // network are the transport's job (AparteDirectTransport / AparteBackendTransport).
        defaultEndpoint: opts.baseURL,

        buildRequest(request: AparteChatRequest) {
            const body: Record<string, unknown> = {
                model: request.modelId,
                messages: toOpenAIMessages(request.messages),
                temperature: request.temperature,
                max_tokens: request.maxTokens,
                stream: request.stream ?? true,
                ...((request.stream ?? true) ? { stream_options: { include_usage: true } } : {}),
                ...(request.seed !== undefined ? { seed: request.seed } : {}),
            };
            if (request.tools?.length) {
                body['tools'] = toOpenAITools(request.tools);
                /*
                 * A forced tool travels; `'auto'` is only the DEFAULT. It used to be
                 * written unconditionally, so `toolChoice: { name }` — a shape the loop
                 * passes straight through, since it intercepts only `{ name, input }`
                 * (the synthetic call it runs itself) — was silently downgraded to
                 * "the model decides", while the ai-sdk bridge honoured the same field.
                 * `'none'` never reaches here: core strips the tools upstream.
                 */
                const forced = typeof request.toolChoice === 'object' ? request.toolChoice.name : undefined;
                body['tool_choice'] = forced ? { type: 'function', function: { name: forced } } : 'auto';
            }
            return {
                path: '/chat/completions',
                body,
                ...(opts.extraHeaders ? { headers: opts.extraHeaders } : {}),
            };
        },

        authHeaders(key: string) {
            return { Authorization: bearer(key) };
        },

        parseStream(body: ReadableStream<Uint8Array>) {
            return parseOpenAICompatStream(body);
        },

        parseText(json: unknown): string {
            return (json as { choices?: Array<{ message?: { content?: string } }> })?.choices?.[0]?.message?.content || '';
        },
    };
}

// ─── SSE stream parser (ported from @aparte/core parseOpenAIStream) ──────────

/**
 * OpenAI-compatible SSE stream parser — this package's own copy of core's
 * `parseOpenAIStream` (the parser follows the format adapter; core keeps only
 * the aparté-native NDJSON parser).
 *
 * Handles:
 * - `delta.content`            → text event
 * - `delta.reasoning_content`  → thinking event (Qwen3, DeepSeek R1, …)
 * - `delta.tool_calls`         → accumulate → tool_use on finish_reason='tool_calls'
 * - usage-only chunk + [DONE]  → done{usage}
 */
export function parseOpenAICompatStream(
    stream: ReadableStream<Uint8Array>,
): ReadableStream<AparteStreamEvent> {
    const decoder = new TextDecoder();
    let buffer = '';

    /*
     * Tool call accumulation state, in wire order, keyed by `toolCallKey` below.
     * A Map, not an object: the key comes off the wire, and on a plain `{}` a chunk
     * whose `index` is `"__proto__"` writes into Object.prototype for the whole page.
     */
    const toolCalls = new Map<string, { id: string; name: string; args: string }>();
    /** The call the last delta touched — what a bare argument fragment continues. */
    let lastToolKey: string | null = null;
    /**
     * Every key a delta has addressed a call by → that call's slot in `toolCalls`.
     * One call can be addressed both ways in one turn (by `index` in a chunk, by `id`
     * in the next), so the identity is the alias, not the key of the moment.
     */
    const toolCallAliases = new Map<string, string>();
    /** Ids minted for a vendor that sends none. Per stream, i.e. per turn. */
    let mintedCallIds = 0;
    let capturedUsage: AparteUsage | undefined;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    /*
     * Emit the accumulated calls and clear the map.
     *
     * Called at EVERY graceful end of a turn, not only on `finish_reason: 'tool_calls'`:
     * the accumulation spans the whole stream, and a vendor that ends with `stop`,
     * `length` or a bare `[DONE]` after streaming tool-call deltas used to drop them —
     * the model had asked for a tool and the loop never heard. `complete` says the
     * model declared the calls finished: a parse failure there is malformed JSON (a
     * real small-model failure) and the tool still runs, on `{}`. Elsewhere it is
     * truncation, and running a tool on `{}` for a call the model never finished would
     * be worse than dropping it — so those are dropped, with a breadcrumb.
     */
    const flushToolCalls = (controller: ReadableStreamDefaultController<AparteStreamEvent>, complete: boolean): void => {
        for (const entry of toolCalls.values()) {
            let input: Record<string, unknown> = {};
            try {
                input = entry.args.trim() ? JSON.parse(entry.args) : {};
            } catch {
                if (!complete) {
                    console.warn(`[openai-compat] Tool "${entry.name}" was cut mid-arguments (the turn ended without finish_reason 'tool_calls'); the call is dropped. Raw:`, entry.args);
                    continue;
                }
                console.warn(
                    `[openai-compat] Tool "${entry.name}" returned malformed arguments JSON; ` +
                    `passing empty input. Raw:`, entry.args,
                );
            }
            const toolCall: AparteToolCall = { id: entry.id, name: entry.name, input };
            controller.enqueue({ type: 'tool_use', ...toolCall });
        }
        toolCalls.clear();
        toolCallAliases.clear();
        lastToolKey = null;
    };

    /*
     * Which accumulated call a `delta.tool_calls` entry belongs to.
     *
     * Both `index` and `id` address a call, and a vendor may use one in a chunk and
     * the other in the next — so neither can BE the identity: a delta's keys are
     * ALIASES that all resolve to the same slot. A key already resolved names the
     * call, and every key a delta carries is recorded against the slot it lands on.
     * Picking one key instead (`index` first, `id` next) split such a call in two,
     * the second half nameless, and the turn ran the tool on `{}`.
     *
     * When no key of the delta is known yet, the function NAME decides — it appears
     * only on a call's FIRST delta, so a nameless delta continues the call before it
     * and a named one opens its own. That single rule covers the vendor whose deltas
     * never carry both addresses at once (open by `id`, continue by `index`, or the
     * reverse) and the vendor that carries neither: `index` is a streaming
     * convenience, not part of the function-call payload, and keying on it alone
     * (`Number(tc.index ?? 0)`) put every call of a server that omits it on slot 0.
     *
     * `null` means "skip this entry": an `index` the vendor sent that is not a
     * non-negative integer is not a slot number, it is malformed (or hostile —
     * `"__proto__"` was the shape that made this a Map).
     */
    const toolCallKey = (tc: { index?: unknown; id?: string; function?: { name?: string } }): string | null => {
        let indexKey: string | undefined;
        if (tc.index !== undefined && tc.index !== null) {
            // Made a number, not annotated as one: `index` is whatever the vendor's JSON put there.
            const idx = Number(tc.index);
            if (!Number.isInteger(idx) || idx < 0) return null;
            indexKey = `i${idx}`;
        }
        const idKey = tc.id ? `d${tc.id}` : undefined;
        const known = (idKey ? toolCallAliases.get(idKey) : undefined) ?? (indexKey ? toolCallAliases.get(indexKey) : undefined);
        // No key of this delta is known yet. A nameless delta never OPENS a call, so it
        // continues the call before it — which is what keeps ONE call together when the
        // vendor opens it with one address and continues it with the other, neither
        // delta carrying both. A named delta still opens its own slot.
        const key = known ?? ((!tc.function?.name && lastToolKey !== null) ? lastToolKey : (idKey ?? indexKey));
        if (key === undefined) return `p${toolCalls.size}`;
        if (idKey) toolCallAliases.set(idKey, key);
        if (indexKey) toolCallAliases.set(indexKey, key);
        return key;
    };

    return new ReadableStream<AparteStreamEvent>({
        async start(controller) {
            reader = stream.getReader();
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() ?? '';

                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed.startsWith('data:')) continue;
                        const raw = trimmed.slice(5).trim();
                        if (raw === '[DONE]') {
                            // A turn that streamed tool-call deltas and ended on `stop`,
                            // `length` or a bare [DONE]: the calls are still the model's.
                            flushToolCalls(controller, false);
                            controller.enqueue({ type: 'done', usage: capturedUsage });
                            return;
                        }
                        try {
                            const json = JSON.parse(raw);

                            // Capture usage from the usage-only chunk (choices: [])
                            if (json.usage) {
                                capturedUsage = {
                                    inputTokens: json.usage.prompt_tokens ?? 0,
                                    outputTokens: json.usage.completion_tokens ?? 0,
                                    totalTokens: json.usage.total_tokens,
                                    cacheReadTokens: json.usage.prompt_tokens_details?.cached_tokens,
                                };
                            }

                            const choice = json.choices?.[0];
                            if (!choice) continue;

                            const delta = choice.delta;
                            if (delta) {
                                if (delta.reasoning_content) {
                                    controller.enqueue({ type: 'thinking', delta: delta.reasoning_content });
                                }
                                if (delta.content) {
                                    controller.enqueue({ type: 'text', delta: delta.content });
                                }
                                if (delta.tool_calls) {
                                    for (const tc of delta.tool_calls) {
                                        const key = toolCallKey(tc);
                                        if (key === null) continue;
                                        let entry = toolCalls.get(key);
                                        if (!entry) {
                                            /*
                                             * An id is MINTED when the vendor sends none. `id: ''`
                                             * used to travel: the transcript keys a row on
                                             * `tool-${id}` and the history a result on
                                             * `toolCallId`, so two id-less calls in one turn shared
                                             * one row (the second call's result landed on the first
                                             * call's line) and one history slot an OpenAI-shaped
                                             * endpoint rejects on the next turn. A real id arriving
                                             * in a later chunk still wins, below. The mint is
                                             * namespaced because `call_1` is what an OpenAI-shaped
                                             * server calls its own first call: a turn mixing an
                                             * id-less call with a real `call_1` minted a duplicate.
                                             */
                                            entry = { id: tc.id ?? `aparte-call-${++mintedCallIds}`, name: tc.function?.name ?? '', args: '' };
                                            toolCalls.set(key, entry);
                                        }
                                        lastToolKey = key;
                                        if (tc.id) entry.id = tc.id;
                                        if (tc.function?.name) entry.name = tc.function.name;
                                        if (tc.function?.arguments) entry.args += tc.function.arguments;
                                    }
                                }
                            }

                            // The model declared the calls complete: emit them now (and clear
                            // the map, so a server that repeats the finish chunk cannot
                            // re-emit them). Do NOT emit `done` or return here: under
                            // `include_usage` (which buildRequest requests) the usage-only
                            // chunk arrives AFTER this finish chunk, so stopping now dropped
                            // usage on every tool-call turn — i.e. most turns of an agent.
                            // Keep reading; the single `done` comes from `[DONE]` below, or
                            // from the end-of-stream fallback if the socket just closes.
                            if (choice.finish_reason === 'tool_calls') flushToolCalls(controller, true);
                        } catch {
                            // Every line here is a complete, newline-terminated SSE
                            // line (see buffer split above), so a parse failure is an
                            // unexpected/malformed server payload, not partial JSON —
                            // log a breadcrumb instead of dropping it silently.
                            console.warn('[openai-compat] Skipped an unparseable SSE data line:', raw);
                        }
                    }
                }
                // The socket closed without [DONE]: same rule, the calls are flushed first.
                flushToolCalls(controller, false);
                controller.enqueue({ type: 'done', usage: capturedUsage });
            } catch (err: unknown) {
                // AbortError surfaces here when the caller's signal fires mid-stream:
                // the consumer (agent loop) cancelled on purpose — end quietly, the
                // same way the ai-sdk provider does. Reporting it as an `error` event
                // makes a deliberate Stop indistinguishable from a network failure,
                // and the loop's error branch then replaces the streamed answer with
                // an error bubble.
                if ((err as { name?: string } | undefined)?.name !== 'AbortError') {
                    controller.enqueue({ type: 'error', message: (err as Error | undefined)?.message ?? 'Stream error' });
                }
            } finally {
                reader?.releaseLock();
                reader = null;
                controller.close();
            }
        },
        // Consumer cancelled (e.g. user hit "stop"): cancel the underlying reader so
        // the vendor response body stops being drained to its natural end instead of
        // silently finishing the whole SSE stream in the background.
        cancel(reason) {
            const r = reader;
            reader = null;
            return r?.cancel(reason);
        },
    });
}

export * from './presets.js';
export type { AparteAIProvider, AparteAIModel } from '@aparte/core';
